import { FormFieldDto, ManagedFragmentDto } from '../../general/dtos';
import { isMultiChoiceType, selectionLabels } from './multi-choice-fields';
import { fieldDisplayNames } from './field-display-names';
import { quizTargetDisplays } from './quiz-targets';
import { markdownHtml } from '../../general/markdown';

export type FragmentAnswers = Record<string, Record<string, any>>;

export function precedingFragments(fragments: ManagedFragmentDto[], currentKey: string | null) {
    const current = fragments.find(fragment => fragment.key === currentKey);
    return current ? fragments.filter(fragment => fragment.key !== currentKey && fragment.order < current.order)
        .sort((a, b) => a.order - b.order) : [];
}

export function readableAnswer(field: FormFieldDto, value: any): string {
    if (value === undefined || value === null || value === '') return '';
    if (isMultiChoiceType(field.type)) return selectionLabels(field, value);
    if (field.type === 'quiz-list') return field.options?.find(option => option.key === String(value))?.value ?? 'Unknown';
    if (field.type === 'checkbox') return value === true || value === 'true' ? 'Yes' : 'No';
    if (field.type === 'select' || field.type === 'radio') {
        return field.options?.find(option => option.value === String(value))?.value
            ?? field.options?.find(option => option.key === String(value))?.value ?? String(value);
    }
    return Array.isArray(value) ? value.join(', ') : String(value);
}

export function renderAnswerTables(doc: Document, fragments: ManagedFragmentDto[], currentKey: string | null, answers: FragmentAnswers) {
    const sources = precedingFragments(fragments, currentKey);
    doc.querySelectorAll('fragment-answers').forEach(placeholder => {
        const source = sources.find(fragment => fragment.key === placeholder.getAttribute('fragment-key'));
        if (!source) {
            const message = doc.createElement('p');
            message.textContent = 'Answer table source is unavailable.';
            placeholder.replaceWith(message);
            return;
        }
        const table = doc.createElement('table');
        table.className = 'fragment-answers-table';
        table.setAttribute('aria-label', `Answers from ${source.title}`);
        const header = table.createTHead().insertRow();
        ['Input label', 'Your answer'].forEach(text => {
            const cell = doc.createElement('th');
            cell.scope = 'col';
            cell.textContent = text;
            header.appendChild(cell);
        });
        const body = table.createTBody();
        const sourceDocument = new DOMParser().parseFromString(source.html, 'text/html');
        const insertedFields = new Set(Array.from(sourceDocument.querySelectorAll('form-field[key]'))
            .map(node => node.getAttribute('key')));
        const names = fieldDisplayNames(source.formFields ?? [], source.html);
        const targets = quizTargetDisplays(source.formFields ?? [], source.html);
        source.formFields?.forEach(field => {
            if (field.type === 'quiz-list') {
                targets.filter(target => target.fieldKey === field.key).forEach(target => {
                    const row = body.insertRow();
                    row.insertCell().innerHTML = markdownHtml(target.label, true);
                    const saved = answers[source.key] ?? {};
                    const nested = saved[field.key];
                    const answer = saved[`${field.key}::${target.expectedKey}`]
                        ?? (nested && typeof nested === 'object' ? nested[target.expectedKey] : undefined);
                    const cell = row.insertCell();
                    cell.textContent = readableAnswer(field, answer) || 'Not answered';
                    cell.style.whiteSpace = 'pre-wrap';
                });
                return;
            }
            if (!insertedFields.has(field.key)) return;
            const row = body.insertRow();
            const labelCell = row.insertCell();
            const name = names[field.key];
            if (name?.context) {
                const highlight = doc.createElement('strong');
                highlight.className = 'inline-blank-highlight';
                highlight.textContent = name.context.blank;
                labelCell.append(doc.createTextNode(name.context.before), highlight, doc.createTextNode(name.context.after));
            } else if (field.type === 'inline-text' && !field.label.trim()) {
                const highlight = doc.createElement('strong');
                highlight.className = 'inline-blank-highlight';
                highlight.textContent = name?.text ?? field.label;
                labelCell.appendChild(highlight);
            } else labelCell.innerHTML = markdownHtml(name?.text ?? field.label, true);
            const value = row.insertCell();
            value.textContent = readableAnswer(field, answers[source.key]?.[field.key]);
            value.style.whiteSpace = 'pre-wrap';
        });
        placeholder.replaceWith(table);
    });
}

export function validateAnswerTableSources(fragments: ManagedFragmentDto[]): string[] {
    return answerTableSourceIssues(fragments).map(issue => issue.message);
}

export function answerTableSourceIssues(fragments: ManagedFragmentDto[]): { fragmentKey: string; message: string }[] {
    const errors: { fragmentKey: string; message: string }[] = [];
    fragments.forEach(fragment => {
        const doc = new DOMParser().parseFromString(fragment.html, 'text/html');
        const sources = precedingFragments(fragments, fragment.key);
        doc.querySelectorAll('fragment-answers').forEach(node => {
            if (!sources.some(source => source.key === node.getAttribute('fragment-key'))) {
                errors.push({ fragmentKey: fragment.key, message: `Fragment "${fragment.title}" has an answer table whose source must be a preceding fragment in this module.` });
            }
        });
    });
    return errors;
}
